import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import categoryData from "../../api/categories";
import { formatPage } from "../../utils/formatPage";
import CategoryDetailMenu from "../../components/CategoryDetailMenu/CategoryDetailMenu";
import NotFound from "../../views/NotFound/404";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";
import { loadProductsWithLangFallback } from "../../utils/productLoader";
import CategoryFilterTop from "../../components/CategoryFilterTop/CategoryFilterTop";
import ProductHoverImage from "./ProductHoverImage";
import Logo1 from "./../../assets/home/test1.png";
import Logo2 from "./../../assets/home/test2.png";
import Finalist1 from "./../../assets/home/finalist1.png";
import Finalist2 from "./../../assets/home/finalist2.png";

const CategoryDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { name } = useParams();
  const categoryName = name;
  const setSeo = useSEO();
  const { currency } = useCurrency(); // conversionRate artık gerekmiyor

  const [products, setProducts] = useState([]);
  const [productsToShow, setProductsToShow] = useState(8);

  const [filters, setFilters] = useState({
    color: "",
    material: "",
    sortBy: "",
  });
  const [filteredProducts, setFilteredProducts] = useState([]);

  const filtersCount = Object.values(filters).filter((v) => v).length;

  const category = categoryData.category.find(
    (c) =>
      categoryName && formatPage(c.categoryname) === formatPage(categoryName),
  );

  useEffect(() => {
    if (category) {
      setSeo({
        title: `${t(category.categoryname)} | Mükemel`,
        description: `${t(category.categorydesc)}`,
        keywords: `${t(category.categorymetakey)}`,
      });
    }
  }, [category, setSeo, t]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await loadProductsWithLangFallback(i18n.language);
      const filtered = data.filter((p) =>
        p.category?.some((cat) => formatPage(cat) === formatPage(categoryName)),
      );
      setProducts(filtered);
    };
    fetchData();
  }, [i18n.language, categoryName]);

  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  const handleLoadMore = () => setProductsToShow((prev) => prev + 8);

  const getUniqueValues = (items, key) => {
    const values = items
      .map((item) => {
        if (key === "material") return item.attributes?.material;
        if (key === "color") return item.attributes?.color;
        return item[key];
      })
      .filter((v) => v !== undefined && v !== null && v !== "");
    return [...new Set(values)];
  };

  const isPerfumeProduct = (p) => {
    const c = p?.category;
    if (Array.isArray(c)) return c.includes("Perfume");
    return typeof c === "string" && c.toLowerCase().includes("perfume");
  };

  // *** YENİ: displayPrices'tan lokasyona göre fiyat çek ***
  const pickDisplayPrice = (product) => {
    if (!product) return null;

    // para birimini normalize et
    let key = String(currency || "TRY").toUpperCase();
    if (key === "TL") key = "TRY";

    // 1) Parfüm ise manual_prices öncelikli
    if (isPerfumeProduct(product) && product.manual_prices) {
      const mp = product.manual_prices?.[key];
      if (mp != null && !Number.isNaN(Number(mp))) {
        return Number(mp);
      }
    }

    // 2) Diğerleri: mevcut displayPrices akışı
    const dp = product.displayPrices;
    if (dp) {
      const scope = key === "TRY" ? "tr" : "intl";
      const val = dp?.[scope]?.[key];
      if (val != null) return Number(val);
      const fallbackUsd = dp?.[scope]?.USD;
      if (fallbackUsd != null) return Number(fallbackUsd);
    }

    // 3) Legacy fallback: plain price alanı
    const legacy = product.price;
    if (legacy != null && legacy !== "") {
      const n = Number(String(legacy).replace(/\./g, "").replace(",", "."));
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };

  const onApplyFilters = () => {
    let result = [...products];

    if (filters.color) {
      result = result.filter((p) => p.color === filters.color);
    }
    if (filters.material) {
      result = result.filter(
        (p) => p.attributes?.material === filters.material,
      );
    }

    if (filters.sortBy === "asc" || filters.sortBy === "desc") {
      result = result
        .map((p) => ({
          p,
          priceVal: pickDisplayPrice(p) ?? Number.POSITIVE_INFINITY,
        }))
        .sort((a, b) =>
          filters.sortBy === "asc"
            ? a.priceVal - b.priceVal
            : b.priceVal - a.priceVal,
        )
        .map(({ p }) => p);
    }

    setFilteredProducts(result);
    setProductsToShow(8);
  };

  const availableFilters = {
    color: getUniqueValues(products, "color"),
    material: getUniqueValues(products, "material"),
  };

  const onResetFilters = () => {
    setFilters({ color: "", material: "", sortBy: "" });
    setFilteredProducts(products);
    setProductsToShow(8);
  };

  const renderProductCard = (product) => {
    const isInStock =
      String(product.availability || "").toLowerCase() ===
      "in stock".toLowerCase();
    const priceToShow = pickDisplayPrice(product);

    return (
      <div
        key={product.id}
        className="col-lg-3 pe-0 px-0 col-md-4 col-sm-6 col-12"
      >
        <a
          href={`/product/${product.id}`}
          className="text-decoration-none text-dark"
        >
          <ProductHoverImage product={product} />
          <div className="bg-light">
            <div className="mx-4">
              <h5 className="pt-2 mb-1">{product.name}</h5>
              {isInStock ? (
                <p className="fw-normal fs-6 mt-1 pb-3 mb-0">
                  {priceToShow !== null ? (
                    <>
                      {Number(priceToShow).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      <span className="mx-1">{currency}</span>
                    </>
                  ) : (
                    <>
                      {Number(
                        product.displayPrices?.intl?.USD || 0,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      <span className="mx-1">USD</span>
                    </>
                  )}
                </p>
              ) : (
                <p className="fw-bold fs-6 mt-1 pb-3 mb-0 text-danger">
                  {t("Out Of Stock")}
                </p>
              )}
            </div>
          </div>
        </a>
      </div>
    );
  };

  if (!category) return <NotFound />;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-12 pe-0 px-0 text-end">
          <CategoryFilterTop
            filters={filters}
            setFilters={setFilters}
            onApply={onApplyFilters}
            onReset={onResetFilters}
            availableFilters={availableFilters}
            filtersCount={filtersCount}
          />
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-8">
          {category.subcategories.length > 0 && (
            <CategoryDetailMenu subcategories={category.subcategories} />
          )}
        </div>
      </div>

      <div className="row">
        {category.subcategories.length > 0
          ? category.subcategories.map((sub) => {
              const subProducts = filteredProducts.filter((product) =>
                product.category?.some(
                  (cat) => formatPage(cat) === formatPage(sub.name),
                ),
              );
              const slicedProducts = subProducts.slice(0, productsToShow);

              return (
                <div key={sub.id} className="pe-0 px-0 pt-5">
                  <h3 className="text-center mb-3 fw-light">{sub.name}</h3>
                  <img
                    src={sub.banner}
                    alt={sub.alt}
                    className="w-100"
                    height={400}
                  />

                  {subProducts.length === 0 ? (
                    <div className="col-lg-12 text-center mt-3">
                      <span className="badge bg-secondary fw-14 text-white fw-light">
                        {t("no_products_found")}
                      </span>
                    </div>
                  ) : (
                    <div className="row">
                      {slicedProducts.map(renderProductCard)}
                      {productsToShow < subProducts.length && (
                        <div className="text-center my-5">
                          <button
                            onClick={handleLoadMore}
                            className="btn fs-2 px-4 py-2"
                          >
                            {t("More")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          : (() => {
              const sliced = filteredProducts.slice(0, productsToShow);
              return (
                <div className="pe-0 px-0 pt-5">
                  <p className="text-uppercase text-center fs-11 mb-0 fw-lighter">
                    MÜKEMEL
                  </p>
                  <h3 className="text-center pt-0 pb-4 fw-light">
                    {t(category.translationKey) || category.categoryname}
                  </h3>

                  {filteredProducts.length === 0 ? (
                    <div className="container">
                      <div className="row justify-content-center py-5">
                        <div className="col-lg-8">
                          <div
                            className="alert alert-danger text-center"
                            role="alert"
                          >
                            {t("ProductNotFound")}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      {sliced.map(renderProductCard)}
                      {productsToShow < filteredProducts.length && (
                        <div className="text-center my-5">
                          <button
                            onClick={handleLoadMore}
                            className="btn fs-2 px-4 py-2"
                          >
                            {t("Show More")}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
        {formatPage(category.categoryname) === "perfume" && (
          <div className="row justify-content-center mb-5 text-center py-4">
            <div className="col-lg-6">
              <h4 className="py-5 pb-2">{t("Finalist")}</h4>
              <img
                src={Finalist1}
                width={110}
                className="me-5"
                alt="Logo of IFF, the fragrance company behind MÜKEMEL Perfume"
              />

              <img
                src={Finalist2}
                width={110}
                alt="Logo of IFF, the fragrance company behind MÜKEMEL Perfume"
              />
            </div>
            <div className="col-lg-6">
              <h4 className="py-5 pb-2">{t("Memberof")}</h4>
              <img
                src={Logo1}
                className="me-5"
                width={150}
                alt="Logo of IFF, the fragrance company behind MÜKEMEL Perfume"
              />

              <img
                src={Logo2}
                width={150}
                alt="Logo of IFF, the fragrance company behind MÜKEMEL Perfume"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryDetailPage;
